import React, { useReducer, useCallback } from 'react'
import { useFormikContext } from 'formik'
import { filter, propEq, prop, map, isNil } from 'ramda'
import { useIntl } from 'react-intl'
import { useAppLogo, useAppName } from 'commons/usePartner'
import effects from 'app/effects/effects'
import { usePlace } from 'commons/usePlace'
import {
  TwitterIcon,
  InstagramIcon,
  LinkedinIcon,
  LineIcon,
  FacebookIcon,
  AutomateCallIcon,
  MessageSpringIcon,
  SmsIcon,
  EmailIcon,
} from 'app/components/icons'
import {
  CHANNEL_APP,
  CHANNEL_SMS,
  CHANNEL_EMAIL,
  CHANNEL_CALL,
  TWITTER_VALUE,
  FACEBOOK_VALUE,
  INSTAGRAM_VALUE,
  LINKEDIN_VALUE,
  LINE_VALUE,
} from 'app/constants/channel'
import { ENABLED_AUTOMATED_CALL } from 'app/constants/compose'
import useFeatureFlag from 'app/configs/useFeatureFlag'

export const actionType = {
  START_LOADING: 'START_LOADING',
  FETCH_OPTION_SUCCESS: 'FETCH_OPTION_SUCCESS',
  ON_OPEN_OPTION: 'ON_OPEN_OPTION',
  ON_CLOSE_OPTION: 'ON_CLOSE_OPTION',
  UPDATE_OPTIONS: 'UPDATE_OPTIONS',
}

const initialValue = {
  options: [],
  isLoading: false,
}

const iconSize = 20

const reducer = (state, action) => {
  switch (action.type) {
    case actionType.START_LOADING:
      return {
        ...state,
        isLoading: true,
      }
    case actionType.FETCH_OPTION_SUCCESS:
      return {
        ...state,
        options: action.options,
        isLoading: false,
      }
    case actionType.ON_OPEN_OPTION:
      return {
        ...state,
        isOpen: true,
      }
    case actionType.ON_CLOSE_OPTION:
      return {
        ...state,
        isOpen: false,
      }
    case actionType.UPDATE_OPTIONS:
      return {
        ...state,
        options: action.options,
      }
    default:
      return state
  }
}

export const useChannels = () => {
  const [state, dispatch] = useReducer(reducer, initialValue)
  const { formatMessage } = useIntl()
  const appName = useAppName()
  const place = usePlace()
  const formik = useFormikContext()
  const { logo: appLogo } = useAppLogo()
  const { mes_3832_new_design_compose_message, mes_4687_FE_line_integration } =
    useFeatureFlag()

  const handleOptions = useCallback(
    ({ pages, socialSetting }) => {
      const pagesActive = filter(propEq('status', 1), prop('facebook', pages))
      const isFacebookOnlyOne = pagesActive.length === 1
      const facebookPages = map((item) => {
        return {
          id: item.id,
          name: !isFacebookOnlyOne
            ? item.name
            : formatMessage({
                id: 'socialMediaIntegrations.provider.facebook.title',
              }),
          icon: isFacebookOnlyOne ? (
            <FacebookIcon />
          ) : (
            <img
              alt={`${item.name}-${item.id}`}
              src={item.imageUrl}
              width={15}
              height={15}
              className="mr-2"
            />
          ),
          iconRevert: isFacebookOnlyOne ? (
            <FacebookIcon />
          ) : (
            <img
              alt={`${item.name}-${item.id}`}
              src={item.imageUrl}
              width={15}
              height={15}
              className="mr-2"
            />
          ),
          parentId: FACEBOOK_VALUE,
          disabled: false,
          subMenu: isFacebookOnlyOne ? item.name : undefined,
          isSubMenu: !isFacebookOnlyOne,
          groupBy: formatMessage({
            id: 'createNotification.socialInput.groups.allSocialMedia',
          }),
        }
      }, pagesActive)
      const facebook =
        socialSetting.facebookConnected &&
        !isFacebookOnlyOne &&
        pagesActive.length > 0
          ? [
              {
                id: FACEBOOK_VALUE,
                name: formatMessage({
                  id: 'socialMediaIntegrations.provider.facebook.title',
                }),
                icon: <FacebookIcon />,
                iconRevert: <FacebookIcon />,
                parentId: null,
                disabled: false,
                subMenu: undefined,
                isSubMenu: false,
                groupBy: formatMessage({
                  id: 'createNotification.socialInput.groups.allSocialMedia',
                }),
              },
            ]
          : []

      const isInstagramOnlyOne = pages.instagram.length === 1
      const instagramPages = map(
        (item) => {
          return {
            id: item.id,
            name: !isInstagramOnlyOne
              ? item.name
              : formatMessage({
                  id: 'socialMediaIntegrations.provider.instagram.title',
                }),
            iconRevert: isInstagramOnlyOne ? (
              <InstagramIcon />
            ) : (
              <img
                alt={`${item.name}-${item.id}`}
                src={item.imageUrl}
                width={15}
                height={15}
                className="mr-2"
              />
            ),
            icon: isInstagramOnlyOne ? (
              <InstagramIcon />
            ) : (
              <img
                alt={`${item.name}-${item.id}`}
                src={item.imageUrl}
                width={15}
                height={15}
                className="mr-2"
              />
            ),
            parentId: INSTAGRAM_VALUE,
            disabled: false,
            subMenu: isInstagramOnlyOne ? item.name : undefined,
            isSubMenu: !isInstagramOnlyOne,
            groupBy: formatMessage({
              id: 'createNotification.socialInput.groups.allSocialMedia',
            }),
          }
        },
        prop('instagram', pages),
      )

      const instagram =
        socialSetting.instagramConnected &&
        !isInstagramOnlyOne &&
        pages.instagram.length > 0
          ? [
              {
                id: INSTAGRAM_VALUE,
                name: formatMessage({
                  id: 'socialMediaIntegrations.provider.instagram.title',
                }),
                icon: <InstagramIcon />,
                iconRevert: <InstagramIcon />,
                parentId: null,
                disabled: false,
                subMenu: undefined,
                isSubMenu: false,
                groupBy: formatMessage({
                  id: 'createNotification.socialInput.groups.allSocialMedia',
                }),
              },
            ]
          : []

      const twitter = socialSetting.twitterConnected
        ? [
            {
              id: TWITTER_VALUE,
              name: formatMessage({
                id: 'socialMediaIntegrations.provider.twitter.title',
              }),
              icon: <TwitterIcon />,
              iconRevert: <TwitterIcon />,
              parentId: null,
              disabled: false,
              subMenu: undefined,
              isSubMenu: false,
              groupBy: formatMessage({
                id: 'createNotification.socialInput.groups.allSocialMedia',
              }),
            },
          ]
        : []

      const linkedin = socialSetting.linkedinConnected
        ? [
            {
              id: LINKEDIN_VALUE,
              name: formatMessage({
                id: 'socialMediaIntegrations.provider.linkedin.title',
              }),
              icon: <LinkedinIcon />,
              iconRevert: <LinkedinIcon />,
              parentId: null,
              subMenu: undefined,
              isSubMenu: false,
              disabled: false,
              groupBy: formatMessage({
                id: 'createNotification.socialInput.groups.allSocialMedia',
              }),
            },
          ]
        : []

      const automatedCall = place[ENABLED_AUTOMATED_CALL]
        ? [
            {
              id: CHANNEL_CALL,
              name: formatMessage({
                id: 'createNotification.socialInput.channels.automatedCall',
              }),
              icon: mes_3832_new_design_compose_message ? (
                <AutomateCallIcon
                  color={'#00B2AD'}
                  classes={`w-${iconSize}`}
                  notBackground={false}
                  backgroundColor={'#183560'}
                />
              ) : (
                <AutomateCallIcon />
              ),
              iconRevert: mes_3832_new_design_compose_message ? (
                <AutomateCallIcon
                  color={'#00B2AD'}
                  classes={`w-${iconSize}`}
                  notBackground={false}
                  backgroundColor={'#183560'}
                />
              ) : (
                <AutomateCallIcon revert={true} />
              ),
              parentId: null,
              disabled: true,
              subMenu: undefined,
              isSubMenu: false,
              groupBy: formatMessage({
                id: 'createNotification.socialInput.groups.allChannels',
              }),
            },
          ]
        : []

      const line =
        !isNil(socialSetting.lineConnected) &&
        socialSetting.lineConnected.status === 1
          ? [
              {
                id: LINE_VALUE,
                name: formatMessage({
                  id: 'subscriber.label.line',
                }),
                icon: <LineIcon />,
                iconRevert: <LineIcon />,
                parentId: null,
                subMenu: undefined,
                isSubMenu: false,
                usaged: socialSetting.lineConnected.usaged,
                credits: socialSetting.lineConnected.credits,
                disabled:
                  Number(socialSetting.lineConnected.usaged) >=
                  Number(socialSetting.lineConnected.credits),
                groupBy: formatMessage({
                  id: 'createNotification.socialInput.groups.allSocialMedia',
                }),
              },
            ]
          : []

      const options = [
        {
          id: CHANNEL_APP,
          name: formatMessage(
            {
              id: 'createNotification.socialInput.channels.notify',
            },
            {
              appName,
            },
          ),
          icon: mes_3832_new_design_compose_message ? (
            <img
              alt="email-icon"
              src={appLogo}
              width={iconSize}
              height={iconSize}
            />
          ) : (
            <MessageSpringIcon />
          ),
          iconRevert: mes_3832_new_design_compose_message ? (
            <img
              alt="email-icon"
              src={appLogo}
              width={iconSize}
              height={iconSize}
            />
          ) : (
            <MessageSpringIcon revert={true} />
          ),
          parentId: null,
          disabled: true,
          subMenu: undefined,
          isSubMenu: false,
          groupBy: formatMessage({
            id: 'createNotification.socialInput.groups.allChannels',
          }),
        },
        {
          id: CHANNEL_SMS,
          name: formatMessage({
            id: 'createNotification.socialInput.channels.sms',
          }),
          icon: mes_3832_new_design_compose_message ? (
            <SmsIcon
              color={'#00B2AD'}
              classes={`w-${iconSize}`}
              notBackground={false}
              backgroundColor={'#183560'}
            />
          ) : (
            <SmsIcon />
          ),
          iconRevert: mes_3832_new_design_compose_message ? (
            <SmsIcon
              color={'#00B2AD'}
              classes={`w-${iconSize}`}
              notBackground={false}
              backgroundColor={'#183560'}
            />
          ) : (
            <SmsIcon revert={true} />
          ),
          parentId: null,
          disabled: true,
          subMenu: undefined,
          isSubMenu: false,
          groupBy: formatMessage({
            id: 'createNotification.socialInput.groups.allChannels',
          }),
        },
        {
          id: CHANNEL_EMAIL,
          name: formatMessage({
            id: 'createNotification.socialInput.channels.email',
          }),
          icon: mes_3832_new_design_compose_message ? (
            <EmailIcon
              color={'#00B2AD'}
              classes={`w-${iconSize}`}
              notBackground={false}
              backgroundColor={'#183560'}
            />
          ) : (
            <EmailIcon />
          ),
          iconRevert: mes_3832_new_design_compose_message ? (
            <EmailIcon
              color={'#00B2AD'}
              classes={`w-${iconSize}`}
              notBackground={false}
              backgroundColor={'#183560'}
            />
          ) : (
            <EmailIcon revert={true} />
          ),
          parentId: null,
          disabled: true,
          subMenu: undefined,
          isSubMenu: false,
          groupBy: formatMessage({
            id: 'createNotification.socialInput.groups.allChannels',
          }),
        },
        ...automatedCall,
        ...facebook,
        ...facebookPages,
        ...instagram,
        ...instagramPages,
        ...twitter,
        ...linkedin,
        ...line,
      ]
      dispatch({ type: actionType.FETCH_OPTION_SUCCESS, options })
    },
    [appName, formatMessage, place, formik.values.notiType],
  )
  /**
   * Fetch API => /social/pages >>
   */
  const fetchSocialProviderPages = useCallback((placeId) => {
    return effects.apis
      .getSocialProviderPages({
        placeId,
      })
      .then((response) => {
        return response
      })
  }, [])

  /**
   * Fetch API => /social/settings >>
   */
  // mes_4687_FE_line_integration
  const fetchSocialIntegrationSettings = useCallback((placeId) => {
    if (mes_4687_FE_line_integration) {
      return effects.apis.integration
        .getSetting({ placeId })
        .then((response) => {
          return response
        })
    } else {
      return effects.apis
        .getSocialIntegrationSettings({ placeId })
        .then((response) => {
          return response
        })
    }
  }, [])

  const getOptions = async ({ placeId, channelSetting }) => {
    dispatch({ type: actionType.START_LOADING })
    const socialSetting = await fetchSocialIntegrationSettings(placeId)
    const pages = await fetchSocialProviderPages(placeId)
    await handleOptions({ pages, socialSetting, channelSetting })
  }

  const onOpenOption = () => {
    dispatch({ type: actionType.ON_OPEN_OPTION })
  }
  const onCloseOption = () => {
    dispatch({ type: actionType.ON_CLOSE_OPTION })
  }
  const updateOptions = (options) => {
    dispatch({ type: actionType.UPDATE_OPTIONS, options })
  }
  return {
    state,
    onOpenOption,
    onCloseOption,
    getOptions,
    updateOptions,
  }
}
